import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Switch,
  Select,
  Divider,
  useToast,
  Skeleton,
  Button,
} from '@chakra-ui/react';
import { PageWrapper } from '@/components/layout';
import { SEO } from '@/components/seo';
import { Bell, Mail, Smartphone, Save, ArrowLeft } from 'lucide-react';
import { useRouter } from '@tanstack/react-router';
import {
  useGetNotificationPreferences,
  useUpdateNotificationPreferences,
  NotificationPreferences,
} from '@/api';
import AppHeader from '@/components/navigation/AppHeader';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

/**
 * #36: the six preference groups on this page each repeated the same card
 * recipe inline. They previously hardcoded `borderRadius="lg"` with no
 * background at all, which rendered as an invisible card in light mode and
 * left `gray.400` body copy stranded on whatever sat behind it. One named
 * surface keeps the six groups in lockstep and gives the next group a single
 * thing to reuse instead of a seventh copy of the same five props.
 */
const SettingsCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    p={6}
    borderRadius="var(--r-card)"
    bg="var(--card-bg)"
    border="var(--card-border)"
    boxShadow="var(--card-shadow)"
    // D66 fix: PageWrapper sets color="white" on its whole subtree for the
    // dark-canvas default. This card is an opaque light surface
    // (--card-bg), so unset-color text inside it (headings, "fontWeight
    // medium" labels) was inheriting that page-level white and rendering
    // invisibly in light mode. Anchoring --card-text here means only
    // elements that don't already set an explicit color (e.g. the
    // --card-text-2 secondary copy) pick it up.
    color="var(--card-text)"
  >
    {children}
  </Box>
);

export function NotificationSettings() {
  const toast = useToast();
  const router = useRouter();
  const { data: preferences, isLoading } = useGetNotificationPreferences();
  const { mutateAsync: updatePreferences, isPending: isSaving } = useUpdateNotificationPreferences();

  const [localPrefs, setLocalPrefs] = useState<NotificationPreferences | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local state when data loads
  useEffect(() => {
    if (preferences) {
      setLocalPrefs(preferences);
    }
  }, [preferences]);

  const handleToggle = (key: keyof NotificationPreferences, value: boolean) => {
    if (!localPrefs) return;
    setLocalPrefs({ ...localPrefs, [key]: value });
    setHasChanges(true);
  };

  const handleFrequencyChange = (frequency: 'instant' | 'daily' | 'weekly' | 'never') => {
    if (!localPrefs) return;
    setLocalPrefs({ ...localPrefs, emailFrequency: frequency });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!localPrefs) return;

    try {
      await updatePreferences(localPrefs);

      analytics.capture(AnalyticsEvents.NOTIFICATION_PREFERENCES_UPDATED, {
        email_frequency: localPrefs.emailFrequency,
      });

      toast({
        title: 'Settings saved!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      setHasChanges(false);
    } catch (error) {
      toast({
        title: 'Failed to save settings',
        description: 'Please try again later',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Failed to save preferences:', error);
    }
  };

  const handleReset = () => {
    if (preferences) {
      setLocalPrefs(preferences);
      setHasChanges(false);
    }
  };

  if (isLoading || !localPrefs) {
    return (
      <PageWrapper>
        <SEO title="Notification Settings" />
        <AppHeader page={'settings'} />
        <Box minH="100vh">
          <Container maxW="container.md" py={6}>
            <VStack spacing={4} align="stretch">
              <Skeleton height="60px" />
              <Skeleton height="60px" />
              <Skeleton height="60px" />
              <Skeleton height="60px" />
            </VStack>
          </Container>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO title="Notification Settings" />
      <AppHeader page={'settings'} />
      <Box minH="100vh">
        <Container maxW="container.md" py={6}>
          <VStack spacing={6} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="center">
            <Button
              leftIcon={<ArrowLeft size={18} />}
              variant="ghost"
              onClick={() => router.history.back()}
              size={{ base: 'sm', lg: 'md' }}
              color="var(--canvas-text)"
              _hover={{ bg: 'var(--color-white-overlay-10)' }}
              _active={{ bg: 'var(--color-white-overlay-20)' }}
            >
              Back
            </Button>
            <Heading size="lg" color="var(--canvas-text)">
              <HStack spacing={2}>
                <Bell size={28} />
                <Text>Notification Settings</Text>
              </HStack>
            </Heading>
            <Box w="60px" /> {/* Spacer for center alignment */}
          </HStack>

          {/* Social Notifications */}
          <SettingsCard>
            <Heading size="md" mb={4}>
              Social Notifications
            </Heading>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Friend Requests</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone sends you a friend request
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.friendRequests}
                  onChange={(e) => handleToggle('friendRequests', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
              <Divider />

              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Friend Accepted</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone accepts your friend request
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.friendAccepted}
                  onChange={(e) => handleToggle('friendAccepted', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
              <Divider />

              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">New Followers</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone follows you
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.newFollowers}
                  onChange={(e) => handleToggle('newFollowers', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
            </VStack>
          </SettingsCard>

          {/* Post Engagement Notifications */}
          <SettingsCard>
            <Heading size="md" mb={4}>
              Post Engagement
            </Heading>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Post Likes</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone likes your post
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.postLikes}
                  onChange={(e) => handleToggle('postLikes', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
              <Divider />

              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Post Comments</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone comments on your post
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.postComments}
                  onChange={(e) => handleToggle('postComments', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
              <Divider />

              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Post Shares</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone shares your post
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.postShares}
                  onChange={(e) => handleToggle('postShares', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
            </VStack>
          </SettingsCard>

          {/* Comment Notifications */}
          <SettingsCard>
            <Heading size="md" mb={4}>
              Comments & Mentions
            </Heading>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Comment Likes</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone likes your comment
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.commentLikes}
                  onChange={(e) => handleToggle('commentLikes', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
              <Divider />

              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Comment Replies</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone replies to your comment
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.commentReplies}
                  onChange={(e) => handleToggle('commentReplies', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
              <Divider />

              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Mentions</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When someone mentions you in a post or comment
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.mentions}
                  onChange={(e) => handleToggle('mentions', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
            </VStack>
          </SettingsCard>

          {/* Activity Notifications */}
          <SettingsCard>
            <Heading size="md" mb={4}>
              Activity
            </Heading>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Check-ins</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    When friends check in at restaurants
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.checkins}
                  onChange={(e) => handleToggle('checkins', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
            </VStack>
          </SettingsCard>

          {/* Email Notifications */}
          <SettingsCard>
            <Heading size="md" mb={4}>
              <HStack spacing={2}>
                <Mail size={20} />
                <Text>Email Notifications</Text>
              </HStack>
            </Heading>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Enable Email Notifications</Text>
                  <Text fontSize="sm" color="var(--card-text-2)">
                    Receive notifications via email
                  </Text>
                </VStack>
                <Switch
                  isChecked={localPrefs.emailEnabled}
                  onChange={(e) => handleToggle('emailEnabled', e.target.checked)}
                  sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
                />
              </HStack>
              <Divider />

              <VStack align="start" spacing={2}>
                <Text fontWeight="medium">Email Frequency</Text>
                <Select
                  value={localPrefs.emailFrequency}
                  onChange={(e) =>
                    handleFrequencyChange(
                      e.target.value as 'instant' | 'daily' | 'weekly' | 'never'
                    )
                  }
                  isDisabled={!localPrefs.emailEnabled}
                >
                  <option value="instant">Instant</option>
                  <option value="daily">Daily digest</option>
                  <option value="weekly">Weekly digest</option>
                  <option value="never">Never</option>
                </Select>
              </VStack>
            </VStack>
          </SettingsCard>

          {/* Push Notifications */}
          <SettingsCard>
            <Heading size="md" mb={4}>
              <HStack spacing={2}>
                <Smartphone size={20} />
                <Text>Push Notifications</Text>
              </HStack>
            </Heading>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontWeight="medium">Enable Push Notifications</Text>
                <Text fontSize="sm" color="var(--card-text-2)">
                  Receive notifications on your device
                </Text>
              </VStack>
              <Switch
                isChecked={localPrefs.pushEnabled}
                onChange={(e) => handleToggle('pushEnabled', e.target.checked)}
                sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'purple.500' } }}
              />
            </HStack>
          </SettingsCard>

          {/* Save Button */}
          {hasChanges && (
            <HStack spacing={3}>
              <Button
                bg="purple.500"
                color="white"
                _hover={{ bg: 'purple.600' }}
                leftIcon={<Save size={20} />}
                onClick={handleSave}
                isLoading={isSaving}
                flex={1}
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                color="var(--canvas-text)"
                borderColor="var(--color-white-overlay-20)"
                _hover={{ bg: 'var(--color-white-overlay-10)' }}
                _active={{ bg: 'var(--color-white-overlay-20)' }}
                onClick={handleReset}
              >
                Cancel
              </Button>
            </HStack>
          )}
          </VStack>
        </Container>
      </Box>
    </PageWrapper>
  );
}
